/**
 * TomasHistoryList — sección "Historial de Tomas" del panel Administrar Cliente.
 *
 * Adopta el design system del config (ver docs/design-system/COMPONENT_CATALOG.md):
 *   ConfigSectionShell (card + header + tab list) → ListingHeader (título + contador +
 *   buscador) → ScrollContainer + <table> → TableNavigation (paginación id="pag-history-listado").
 *
 * Acción por fila: Calcular (recalcula el conteo de catalogado). El botón muerto "Catalogar"
 * (sin onClick) se removió; la acción IN/OUT se eliminó con la feature (PT-#95).
 *
 * Reporte enriquecido (Cobertura%, Costo USD, fail%, Ocultos, Estado, runId) pendiente:
 * requiere endpoint de agregación backend — ver docs/pendientes/historial-reporte-enriquecido.md.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Database,
  List,
  RefreshCw,
  Loader2,
} from 'lucide-react';
import api from '../../utils/api';
import ConfigSectionShell from '../common/ConfigSectionShell';
import ListingHeader from '../common/ListingHeader';
import ScrollContainer from '../common/ScrollContainer';
import TableNavigation from '../TableNavigation';

interface TomaData {
  tomaNumber: number;
  tableName: string;
  fecha: string | null;
  recordCount: number;
  catalogadoCount: number;
}

interface Props {
  clientId: string;
  clientName?: string;
}

const formatDate = (input: string | Date | null): string => {
  if (!input) return '—';
  try {
    const d = typeof input === 'string' ? new Date(input) : input;
    if (isNaN(d.getTime())) return '—';
    const hasTime = d.getUTCHours() !== 0 || d.getUTCMinutes() !== 0;
    const opts: Intl.DateTimeFormatOptions = {
      day: '2-digit', month: '2-digit', year: 'numeric',
    };
    if (hasTime) { opts.hour = '2-digit'; opts.minute = '2-digit'; opts.hour12 = false; }
    return new Intl.DateTimeFormat('es-AR', opts).format(d);
  } catch {
    return '—';
  }
};

const TABS = [{ id: 'listado', label: 'Listado', icon: List }];

const TomasHistoryList: React.FC<Props> = ({ clientId, clientName }) => {
  const [tomas, setTomas] = useState<TomaData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [calculatingCatalogado, setCalculatingCatalogado] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/api/clients/${clientId}/tomas?detailed=true&limit=50`, {
        headers: { 'x-client-id': clientId },
        timeout: 30000,
      });
      if (res.data?.success) {
        const arr: TomaData[] = (res.data.tomas || []).map((t: any) => ({
          tomaNumber: t.tomaNumber ?? t.numero ?? 0,
          tableName: t.tableName ?? '',
          fecha: t.fecha ?? null,
          recordCount: t.recordCount ?? 0,
          catalogadoCount: t.catalogadoCount ?? 0,
        })).sort((a: TomaData, b: TomaData) => (b.tomaNumber || 0) - (a.tomaNumber || 0));
        setTomas(arr);
      } else {
        setError(res.data?.error || 'Error al cargar tomas');
      }
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const calculateCatalogado = async (toma: TomaData) => {
    setCalculatingCatalogado(toma.tomaNumber);
    try {
      await api.post(`/api/clients/${clientId}/tomas/${toma.tomaNumber}/calculate-catalogado`, {}, {
        headers: { 'x-client-id': clientId },
      });
      await load();
    } catch (e: any) {
      alert('Error calculando catalogado: ' + (e?.message || 'desconocido'));
    } finally {
      setCalculatingCatalogado(null);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tomas;
    return tomas.filter((t) =>
      String(t.tomaNumber).includes(q) || (formatDate(t.fecha).toLowerCase().includes(q))
    );
  }, [tomas, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(pageIndex, totalPages - 1);
  const pageRows = filtered.slice(safePage * pageSize, safePage * pageSize + pageSize);

  const refreshBtn = (
    <button
      onClick={load}
      disabled={loading}
      className="flex items-center gap-1 px-2 py-1.5 text-xs text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
    >
      <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
      Refrescar
    </button>
  );

  const renderBody = () => {
    if (loading && tomas.length === 0) {
      return (
        <div className="p-8 flex items-center justify-center gap-2 text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span className="text-sm">Cargando historial...</span>
        </div>
      );
    }
    if (error) {
      return (
        <div className="bg-red-50 rounded-lg border border-red-200 p-4">
          <p className="text-sm text-red-700">{error}</p>
          <button onClick={load} className="mt-2 px-3 py-1 text-xs bg-red-100 hover:bg-red-200 text-red-800 rounded">
            Reintentar
          </button>
        </div>
      );
    }
    if (tomas.length === 0) {
      return (
        <div className="p-8 text-center">
          <Database className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">Sin tomas registradas{clientName ? ` para ${clientName}` : ''}</p>
        </div>
      );
    }

    return (
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <ListingHeader
          count={filtered.length}
          total={tomas.length}
          itemNoun="toma"
          search={{ value: search, onChange: (v) => { setSearch(v); setPageIndex(0); }, placeholder: 'Buscar toma o fecha...' }}
          actions={refreshBtn}
        />
        <TableNavigation
          id="pag-history-listado"
          pageIndex={safePage}
          pageSize={pageSize}
          totalRows={filtered.length}
          totalPages={totalPages}
          onPageChange={setPageIndex}
          onPageSizeChange={(s) => { setPageSize(s); setPageIndex(0); }}
          position="top"
        />
        <ScrollContainer>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200 text-left text-xs text-gray-500 uppercase tracking-wide">
                <th className="px-3 py-2 font-medium">Toma</th>
                <th className="px-3 py-2 font-medium">Fecha</th>
                <th className="px-3 py-2 font-medium text-right">Items</th>
                <th className="px-3 py-2 font-medium text-right">Catalogado</th>
                <th className="px-3 py-2 font-medium text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((toma, index) => {
                const catalogadoPct = toma.recordCount > 0 ? Math.round((toma.catalogadoCount / toma.recordCount) * 100) : 0;
                return (
                  <tr
                    key={`${toma.tableName || toma.tomaNumber || index}`}
                    className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-3 py-2 font-semibold text-gray-900 whitespace-nowrap">#{toma.tomaNumber}</td>
                    <td className="px-3 py-2 text-gray-700 tabular-nums whitespace-nowrap">{formatDate(toma.fecha)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-gray-700">{(toma.recordCount || 0).toLocaleString('es-AR')}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold text-purple-700 whitespace-nowrap">
                      {(toma.catalogadoCount || 0).toLocaleString('es-AR')}
                      <span className="text-purple-400 font-normal"> de </span>
                      {(toma.recordCount || 0).toLocaleString('es-AR')}
                      {toma.recordCount > 0 && <span className="text-[11px] text-purple-400 ml-1">· {catalogadoPct}%</span>}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          onClick={() => calculateCatalogado(toma)}
                          disabled={calculatingCatalogado === toma.tomaNumber}
                          title="Recalcular conteo de catalogado"
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded transition-colors shadow-sm bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-50"
                        >
                          {calculatingCatalogado === toma.tomaNumber
                            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            : <RefreshCw className="w-3.5 h-3.5" />}
                          Calcular
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </ScrollContainer>
      </div>
    );
  };

  return (
    <ConfigSectionShell
      icon={Database}
      title="Historial de Tomas"
      clientName={clientName}
      tabs={TABS}
      activeTab="listado"
    >
      {renderBody()}
    </ConfigSectionShell>
  );
};

export default TomasHistoryList;
